import React from 'react';
import { Link } from 'react-router-dom';
import { Tag, Tooltip } from 'antd';
import { formatCurrencyGrouped } from '../../../utils/currency';
import type {
  ForecastVsActual,
  ForecastVsActualOrder,
  ForecastVsActualOrderTotals,
  IncomeStatementPurchase,
} from './incomeStatementApi';
import { monthShortLabel } from './incomeStatement';

// WP6 "Prognoza a wynik" (docs/decisions/2026-09-24-wp6-prognoza-a-wynik.md).
// Renders the backend block as is: which order is forecast or actual, and
// every amount, is decided by incomeStatementService. An unknown cost is shown
// as "Koszt nieznany", never as zero, and ZK cost of goods is never added to
// ZD purchases.

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

const K = 'reports:profitability.incomeStatement.forecastVsActual';

interface Props {
  block: ForecastVsActual;
  purchases: IncomeStatementPurchase[];
  periodLabel: string;
  language: string;
  t: TranslateFn;
}

function money(value: number | null | undefined): string {
  return value == null ? '—' : formatCurrencyGrouped(value);
}

function signedMoney(value: number): string {
  const formatted = formatCurrencyGrouped(Math.abs(value));
  if (value > 0) return `+${formatted}`;
  if (value < 0) return `−${formatted}`;
  return formatted;
}

function monthLabel(month: string | null, language: string): string {
  return month ? monthShortLabel(month, language) : '—';
}

function CostUnknown({ label, hint }: { label: string; hint: string }) {
  return (
    <Tooltip title={hint}>
      <span className="forecast-vs-actual__unknown" data-testid="forecast-vs-actual-cost-unknown">{label}</span>
    </Tooltip>
  );
}

function TotalsCells({ totals, t }: { totals: ForecastVsActualOrderTotals; t: TranslateFn }) {
  return (
    <>
      <td>{money(totals.revenue)}</td>
      <td>
        {totals.totalCost == null
          ? <CostUnknown label={t(`${K}.costUnknown`)} hint={t(`${K}.pendingCount`, { count: totals.pendingCostOrderCount })} />
          : money(totals.totalCost)}
      </td>
      <td className={totals.margin != null && totals.margin < 0 ? 'is-negative' : ''}>{money(totals.margin)}</td>
    </>
  );
}

function OrderIdentity({ order, t }: { order: ForecastVsActualOrder; t: TranslateFn }) {
  return (
    <div className="forecast-vs-actual__identity">
      <Link to={`/app/orders/${order.id}`}>{order.displayOrderNumber}</Link>
      <span className="forecast-vs-actual__meta">
        <span>{order.customer?.name || t(`${K}.noCustomer`)}</span>
        {' · '}
        <span>{t(`orders:statusOptions.${order.status}`, { defaultValue: order.status })}</span>
      </span>
    </div>
  );
}

function DeltaCell({ order, t }: { order: ForecastVsActualOrder; t: TranslateFn }) {
  if (!order.planned) return <td className="forecast-vs-actual__muted">{t(`${K}.noSnapshot`)}</td>;
  const delta = order.delta?.margin ?? null;
  return (
    <td className={delta != null && delta < 0 ? 'is-negative' : ''}>
      <div className="forecast-vs-actual__stack">
        <span>{delta == null ? '—' : signedMoney(delta)}</span>
        <span className="forecast-vs-actual__meta">
          {order.planned.margin == null
            ? t(`${K}.plannedCostUnknown`)
            : t(`${K}.plannedMargin`, { amount: money(order.planned.margin) })}
        </span>
      </div>
    </td>
  );
}

function IssuedOverInvoiceNote({ order, t }: { order: ForecastVsActualOrder; t: TranslateFn }) {
  if (order.issuedOverInvoiceCostPLN == null) return null;
  return (
    <span className="forecast-vs-actual__meta" data-testid="forecast-vs-actual-issued-over-invoice-order">
      {t(`${K}.issuedOverInvoiceOrder`, { amount: money(order.issuedOverInvoiceCostPLN) })}
    </span>
  );
}

function ActualCostCell({ order, t }: { order: ForecastVsActualOrder; t: TranslateFn }) {
  if (order.totalCost != null) {
    if (order.issuedOverInvoiceCostPLN == null) return <td>{money(order.totalCost)}</td>;
    return (
      <td>
        <div className="forecast-vs-actual__stack">
          <span>{money(order.totalCost)}</span>
          <IssuedOverInvoiceNote order={order} t={t} />
        </div>
      </td>
    );
  }
  return (
    <td>
      <div className="forecast-vs-actual__stack">
        <CostUnknown label={t(`${K}.ledgerPending`)} hint={t(`${K}.ledgerPendingHint`)} />
        {order.knownCost != null && (
          <span className="forecast-vs-actual__meta">{t(`${K}.knownCost`, { amount: money(order.knownCost) })}</span>
        )}
        {order.unissued && (
          <span className="forecast-vs-actual__meta" data-testid="forecast-vs-actual-unissued">
            {order.unissued.costUnknown || order.unissued.averageCostPLN == null
              ? t(`${K}.unissuedUnknown`)
              : t(`${K}.unissuedEstimate`, { amount: money(order.unissued.averageCostPLN) })}
          </span>
        )}
        <IssuedOverInvoiceNote order={order} t={t} />
      </div>
    </td>
  );
}

function OrdersTable({ orders, basis, language, t }: {
  orders: ForecastVsActualOrder[];
  basis: 'forecast' | 'actual';
  language: string;
  t: TranslateFn;
}) {
  return (
    <div className="profitability-income-firms-wrap">
      <table className="profitability-income-firms forecast-vs-actual__table">
        <thead>
          <tr>
            <th>{t(`${K}.cols.order`)}</th>
            <th>{t(`${K}.${basis}.colMonth`)}</th>
            <th>{t(`${K}.cols.revenue`)}</th>
            <th>{t(`${K}.cols.cost`)}</th>
            <th>{t(`${K}.cols.margin`)}</th>
            <th>{t(`${K}.cols.delta`)}</th>
          </tr>
        </thead>
        <tbody>
          {orders.length === 0 ? (
            <tr><td colSpan={6} className="profitability-income-empty">{t(`${K}.${basis}.empty`)}</td></tr>
          ) : orders.map((order) => (
            <tr key={order.id} data-order-id={order.id}>
              <td><OrderIdentity order={order} t={t} /></td>
              <td>{monthLabel(order.month, language)}</td>
              <td>{money(order.revenue)}</td>
              {basis === 'actual' ? <ActualCostCell order={order} t={t} /> : (
                <td>
                  {order.totalCost == null
                    ? <CostUnknown label={t(`${K}.costUnknown`)} hint={t(`${K}.costUnknownHint`)} />
                    : money(order.totalCost)}
                </td>
              )}
              <td className={order.margin != null && order.margin < 0 ? 'is-negative' : ''}>{money(order.margin)}</td>
              <DeltaCell order={order} t={t} />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PurchasesTable({ rows, basis, language, t }: {
  rows: IncomeStatementPurchase[];
  basis: 'forecast' | 'actual';
  language: string;
  t: TranslateFn;
}) {
  return (
    <div className="profitability-income-firms-wrap">
      <table className="profitability-income-firms forecast-vs-actual__table">
        <caption className="forecast-vs-actual__caption">{t(`${K}.purchases.${basis}Title`)}</caption>
        <thead>
          <tr>
            <th>{t(`${K}.purchases.colSupplierOrder`)}</th>
            <th>{t(`${K}.purchases.colMonth`)}</th>
            <th>{t(`${K}.purchases.colInvoice`)}</th>
            <th>{t(`${K}.purchases.colAmount`)}</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={4} className="profitability-income-empty">{t(`${K}.purchases.${basis}Empty`)}</td></tr>
          ) : rows.map((row) => (
            <tr key={`${row.kind}-${row.supplierOrderId}-${row.invoiceId || 'plan'}`}>
              <td>
                <div className="forecast-vs-actual__identity">
                  <Link to={`/app/supplier-orders/${row.supplierOrderId}`}>{row.orderNumber}</Link>
                  <span className="forecast-vs-actual__meta">{row.supplierName}</span>
                </div>
              </td>
              <td>{monthLabel(row.month, language)}</td>
              <td>{row.invoiceId ? <Link to={`/app/purchase-invoices/${row.invoiceId}`}>{row.documentNumber}</Link> : '—'}</td>
              <td>{money(row.netPLN + row.extraCostPLN)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const ForecastVsActualSection: React.FC<Props> = ({ block, purchases, periodLabel, language, t }) => {
  const forecastPurchases = purchases.filter((row) => !row.actual);
  const actualPurchases = purchases.filter((row) => row.actual);
  // A month with no order and no purchase on either side is an empty row;
  // height is the scarce dimension (DESIGN.md), the total row stays.
  const activeMonths = block.months.filter((month) => (
    month.forecast.orders.orderCount > 0 || month.actual.orders.orderCount > 0
    || month.forecast.purchases.count > 0 || month.actual.purchases.count > 0
  ));

  return (
    <section
      className="profitability-income__section forecast-vs-actual"
      aria-label={t(`${K}.kicker`)}
      data-testid="forecast-vs-actual"
    >
      <div className="profitability-income__section-head">
        <div>
          <span className="profitability-income__eyebrow">{t(`${K}.kicker`)}</span>
          <h3>{t(`${K}.title`)}</h3>
          <p>{t(`${K}.hint`)}</p>
        </div>
      </div>

      <div className="profitability-income__panel">
        <div className="profitability-income-firms-wrap">
          <table className="profitability-income-firms forecast-vs-actual__months" data-testid="forecast-vs-actual-months">
            <thead>
              <tr className="forecast-vs-actual__groups">
                <th rowSpan={2}>{t(`${K}.months.colMonth`)}</th>
                <th colSpan={3}>{t(`${K}.months.groupForecast`)}</th>
                <th colSpan={3}>{t(`${K}.months.groupActual`)}</th>
                <th colSpan={2}>
                  {t(`${K}.months.groupPurchases`)}
                  <span className="forecast-vs-actual__group-note">{t(`${K}.months.purchasesNote`)}</span>
                </th>
              </tr>
              <tr>
                <th>{t(`${K}.months.colRevenue`)}</th>
                <th>{t(`${K}.months.colCost`)}</th>
                <th>{t(`${K}.months.colMargin`)}</th>
                <th>{t(`${K}.months.colRevenue`)}</th>
                <th>{t(`${K}.months.colCost`)}</th>
                <th>{t(`${K}.months.colMargin`)}</th>
                <th>{t(`${K}.months.colPurchaseForecast`)}</th>
                <th>{t(`${K}.months.colPurchaseActual`)}</th>
              </tr>
            </thead>
            <tbody>
              {activeMonths.length === 0 && (
                <tr><td colSpan={9} className="profitability-income-empty">{t(`${K}.months.empty`)}</td></tr>
              )}
              {activeMonths.map((month) => (
                <tr key={month.month}>
                  <td>{monthShortLabel(month.month, language)}</td>
                  <TotalsCells totals={month.forecast.orders} t={t} />
                  <TotalsCells totals={month.actual.orders} t={t} />
                  <td>{money(month.forecast.purchases.amountPLN)}</td>
                  <td>{money(month.actual.purchases.amountPLN)}</td>
                </tr>
              ))}
              <tr className="forecast-vs-actual__total">
                <td>{t(`${K}.months.total`)} · {periodLabel}</td>
                <TotalsCells totals={block.totals.forecast.orders} t={t} />
                <TotalsCells totals={block.totals.actual.orders} t={t} />
                <td>{money(block.totals.forecast.purchases.amountPLN)}</td>
                <td>{money(block.totals.actual.purchases.amountPLN)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <section className="profitability-income__panel forecast-vs-actual__part" aria-label={t(`${K}.forecast.title`)}>
        <header className="forecast-vs-actual__part-head">
          <h4>
            {t(`${K}.forecast.title`)}
            {' '}
            {block.totals.forecast.orders.pendingCostOrderCount > 0 && (
              <Tag color="warning">{t(`${K}.pendingCount`, { count: block.totals.forecast.orders.pendingCostOrderCount })}</Tag>
            )}
          </h4>
          <p>{t(`${K}.forecast.hint`)}</p>
        </header>
        <OrdersTable orders={block.orders.forecast} basis="forecast" language={language} t={t} />
        <PurchasesTable rows={forecastPurchases} basis="forecast" language={language} t={t} />
      </section>

      <section className="profitability-income__panel forecast-vs-actual__part" aria-label={t(`${K}.actual.title`)}>
        <header className="forecast-vs-actual__part-head">
          <h4>
            {t(`${K}.actual.title`)}
            {' '}
            {block.totals.actual.orders.pendingCostOrderCount > 0 && (
              <Tag color="warning">{t(`${K}.pendingCount`, { count: block.totals.actual.orders.pendingCostOrderCount })}</Tag>
            )}
          </h4>
          <p>{t(`${K}.actual.hint`)}</p>
          {block.issuedOverInvoice.orderCount > 0 && (
            <Tooltip title={t(`${K}.actual.issuedOverInvoiceHint`)}>
              <p className="forecast-vs-actual__unknown forecast-vs-actual__notice" data-testid="forecast-vs-actual-issued-over-invoice">
                {t(`${K}.actual.issuedOverInvoice`, {
                  amount: money(block.issuedOverInvoice.costPLN),
                  count: block.issuedOverInvoice.orderCount,
                })}
              </p>
            </Tooltip>
          )}
        </header>
        <OrdersTable orders={block.orders.actual} basis="actual" language={language} t={t} />
        <PurchasesTable rows={actualPurchases} basis="actual" language={language} t={t} />
      </section>
    </section>
  );
};

export default ForecastVsActualSection;
